import { Fragment, useId, type ReactNode } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { MoreVerticalIcon } from 'lucide-react';
import { TruncatedText } from '@/shared/ui';
import { PermissionTooltip } from '../ScopedPermission/PermissionTooltip';
import { ThumbnailImage } from '../ThumbnailImage';
import type { AssetCardAction, AssetCardProps } from './AssetCard.types';
import { useTranslation } from 'react-i18next';

interface ActionGroup {
  label: string | undefined;
  entries: { action: AssetCardAction; index: number }[];
}

function groupActions(actions: AssetCardAction[]): ActionGroup[] {
  return actions.reduce<ActionGroup[]>((groups, action, index) => {
    const last = groups.at(-1);
    if (last && last.label === action.groupLabel) {
      last.entries.push({ action, index });
      return groups;
    }
    groups.push({ label: action.groupLabel, entries: [{ action, index }] });
    return groups;
  }, []);
}

export function AssetCard(props: AssetCardProps) {
  const { t } = useTranslation('common');
  const menuId = useId();

  const {
    thumbnailUrl,
    thumbnailAlt,
    thumbnailFallback,
    thumbnail,
    thumbnailOverlay,
    thumbnailBg = 'bg-secondary',
    title,
    subtext,
    description,
    tags,
    actions,
    actionsAriaLabel,
    onActionsOpenChange,
    onClick,
    className,
  } = props;

  const hasActions = !!actions && actions.length > 0;
  const hasGroupedActions = !!actions?.some((action) => !!action.groupLabel);
  const hasHeader = subtext != null || hasActions;
  const thumbnailLabel = thumbnailAlt ?? title;

  let thumbnailContent: ReactNode;
  if (thumbnail != null) {
    thumbnailContent = thumbnail;
  } else if (thumbnailUrl) {
    thumbnailContent = (
      <ThumbnailImage
        src={thumbnailUrl}
        alt={thumbnailLabel}
        className="h-full w-full"
        fit="contain"
        fallback={thumbnailFallback}
      />
    );
  } else {
    thumbnailContent = thumbnailFallback;
  }

  // The slot insets the thumbnail block (`p-3`) so it floats inside the card;
  // the block itself carries the rounded `bg-secondary` surface. This inset is
  // the single source of thumbnail spacing — widget thumbnails must NOT add
  // their own padding on top of it.
  // Pin the single grid cell to the block's own width AND height with
  // `grid-cols-1`/`grid-rows-1` (both minmax(0,1fr)) so neither wide text nor a
  // tall image can blow the cell past the card; `justify-self` on the child
  // then centers the white page within it.
  const thumbnailInner = (
    <div
      className={clsx(
        'grid grid-cols-1 grid-rows-1 h-full w-full overflow-hidden rounded-lg',
        thumbnailBg,
      )}
    >
      {thumbnailContent}
    </div>
  );

  return (
    <div
      onClick={
        onClick
          ? () => {
              if (!window.getSelection()?.toString()) onClick();
            }
          : undefined
      }
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick();
            }
          : undefined
      }
      className={clsx(
        'flex flex-col overflow-hidden rounded-lg transition-[border-color] duration-fast ease-out border border-border-light hover:border-border-strong bg-card',
        onClick && 'cursor-pointer',
        className,
      )}
    >
      <div aria-label={thumbnailLabel} className="relative h-48 shrink-0 overflow-hidden p-3 pb-0">
        {thumbnailInner}
        {thumbnailOverlay}
      </div>

      <div className="flex flex-1 flex-col p-4">
        {hasHeader && (
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1 text-body-md text-foreground">{subtext}</div>
            {hasActions && (
              <DropdownMenu onOpenChange={onActionsOpenChange}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="default"
                    aria-label={actionsAriaLabel ?? t('components.assetCard.moreActions')}
                    className="size-xl p-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVerticalIcon className="size-md" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className={hasGroupedActions ? 'w-56' : 'w-44'}
                  onClick={(e) => e.stopPropagation()}
                >
                  {groupActions(actions).map((group, groupIndex) => {
                    const labelId = `${menuId}-group-${groupIndex}`;
                    const items = group.entries.map(({ action, index }) => (
                      <Fragment key={`${action.label}-${index}`}>
                        {action.topDivider && index > 0 && (
                          <DropdownMenuSeparator className="mx-1.5" />
                        )}
                        <PermissionTooltip
                          disabled={!!action.disabled && !!action.disabledTooltip}
                          message={action.disabledTooltip}
                        >
                          <DropdownMenuItem
                            onSelect={action.onSelect}
                            disabled={action.disabled}
                            className={action.destructive ? 'text-destructive' : undefined}
                          >
                            {action.icon && (
                              <span className="flex size-md items-center">{action.icon}</span>
                            )}
                            {action.label}
                          </DropdownMenuItem>
                        </PermissionTooltip>
                      </Fragment>
                    ));

                    if (group.label === undefined) {
                      return <Fragment key={labelId}>{items}</Fragment>;
                    }

                    return (
                      <DropdownMenuGroup key={labelId} aria-labelledby={labelId}>
                        <DropdownMenuLabel
                          id={labelId}
                          className="text-muted-foreground text-xs font-normal"
                        >
                          {group.label}
                        </DropdownMenuLabel>
                        {items}
                      </DropdownMenuGroup>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}

        <div className="flex min-w-0 flex-col gap-1 min-h-[48px] mb-5">
          <TruncatedText value={title} className="text-heading-sm font-medium text-foreground" />
          {description != null && (
            <div className="line-clamp-1 text-xs text-muted-foreground">{description}</div>
          )}
        </div>

        {/* Tag spec (Figma): body/md text (text-sm, weight 400), the smallest
            badge inset (8px, `px-2`), and a 24px pill height
            (20px line + 2px border + `py-px`). The child overrides beat each
            badge's own `text-xs font-medium px-2.5 py-0.5` defaults. */}
        {tags != null && (
          <div className="mt-auto flex flex-wrap items-center gap-1 *:text-sm *:font-normal *:py-px">
            {tags}
          </div>
        )}
      </div>
    </div>
  );
}
